// @ts-check
"use strict";

import React from "react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useLinkPreview, getFallbackData } from "../../services/useLinkPreview";

/**
 * @typedef {Object} CitationData
 * @property {string} title - The title of the citation
 * @property {string} url - The URL of the citation
 */

/**
 * Custom cite component with tooltip functionality
 * @param {Object} props
 * @param {string} props.number - The citation number to display
 * @param {string} props.url - The URL to link to
 * @param {CitationData} props.citation - The citation data for the tooltip
 */
export default function ChatBubbleCite({ number, url, citation }) {
  const { data: linkPreviewData, isLoading, error } = useLinkPreview(url);
  const linkPreview = linkPreviewData ?? getFallbackData(url);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <cite className="inline">
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center border px-1.5 py-0.5 text-[0.625rem] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground/80 ml-0.5 rounded-full no-underline"
          >
            {linkPreview.domain}
          </a>
        </cite>
      </TooltipTrigger>
      <TooltipContent 
        side="top" 
        className="max-w-[20rem] min-w-[10rem] rounded-lg border border-border bg-popover px-2.5 py-2.5 text-popover-foreground shadow-md"
        sideOffset={8}
      >
        <div className="space-y-2">
          <p className="text-sm font-medium leading-tight text-wrap line-clamp-2">
            {linkPreview.title}
          </p>
          {linkPreview?.description && (
            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
              {linkPreview.description}
            </p>
          )}
          <div className="flex items-center gap-2">
            <div className="shrink-0">
              {linkPreview.favicon && (
                <img 
                  src={linkPreview.favicon} 
                  alt={`${linkPreview.domain} icon`}
                  className="w-4 h-4 rounded-sm object-contain"
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {linkPreview.domain}
            </p>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}